/**
 * ワークスペース Hooks スキーマ — Claude Code Hooks 互換
 */
import { z } from 'zod';
// ========================================
// イベント種別
// ========================================
/**
 * フックイベント種別（13種 — Claude Code Hooks 互換）
 *
 * 公式イベントとの対応:
 * - SessionStart: セッション（タスク）開始時
 * - UserPromptSubmit: プロンプト送信時（処理開始前）
 * - Stop: Claude の応答完了時（ターンごと）
 * - StopFailure: API エラーでターンが終了した時
 * - PreToolUse / PostToolUse / PostToolUseFailure: ツール実行前後
 * - SubagentStart / SubagentStop: サブエージェントの開始・終了
 * - PreCompact / PostCompact: コンテキスト圧縮の前後
 * - ApprovalRequired: ツール承認待ちに入る時（ユーザーの承認判断を待機開始）
 *   matcherValue: 承認対象のツール名
 * - UserInputRequired: 質問回答待ちに入る時（askUserInput / askConfirmation の開始）
 *   matcherValue: 'userInput' | 'confirmation'
 */
export const HookEventTypeSchema = z.enum([
    'SessionStart',
    'UserPromptSubmit',
    'PreToolUse',
    'PostToolUse',
    'PostToolUseFailure',
    'Stop',
    'StopFailure',
    'SubagentStart',
    'SubagentStop',
    'PreCompact',
    'PostCompact',
    'ApprovalRequired',
    'UserInputRequired',
]);
/** 全イベント種別の配列 */
export const HOOK_EVENT_TYPES = HookEventTypeSchema.options;
// ========================================
// フック定義
// ========================================
/**
 * フック定義（Claude Code hooks.json 互換）
 */
export const HookDefinitionSchema = z.object({
    type: z.literal('command'),
    command: z
        .string()
        .min(1, 'command is required')
        .describe('コンテナサンドボックス内で bash -c 経由で実行されるシェルコマンド（ホストOS上では実行されない）。' +
        'イベントコンテキストのJSONは標準入力から渡される。判断プロトコル: ' +
        '終了コード0かつ標準出力が {"decision":"allow"|"deny","reason"?,"additionalContext"?} 形式のJSON→構造化結果として解釈。' +
        '終了コード0かつ非JSON・非空の標準出力→全体をadditionalContext（AIへの追加情報）として扱いdecisionは暗黙allow。' +
        '終了コード2→常にdeny、reasonは標準エラー出力全体。それ以外の非ゼロ終了→実行エラー扱い（ワークスペースのフェイルセーフ設定に従う）。' +
        '外部スクリプトを参照する場合はコンテナから見えるパス（ボリュームマウント経由）を指定するか、コマンド文字列にインライン化すること。'),
    timeout: z.number().optional(),
    enabled: z.boolean().default(true),
});
// ========================================
// フックグループ
// ========================================
/**
 * フックグループ（マッチャー付き）
 */
export const HookGroupSchema = z.object({
    matcher: z
        .string()
        .optional()
        .describe('対象を絞り込む正規表現（例: "web-fetch|web-search"）。省略・空文字列は常にマッチ（全ツール対象）。' +
        'PreToolUse/PostToolUse系ではツール名、SubagentStart/Stopではサブエージェント名、' +
        'ApprovalRequiredではツール名、UserInputRequiredでは "userInput" | "confirmation" と照合される。'),
    hooks: z.array(HookDefinitionSchema).min(1, 'hooks must have at least one entry'),
});
// ========================================
// ワークスペースフック設定
// ========================================
/**
 * ワークスペースフック設定（hooks.json 互換構造）
 *
 * イベント種別をキーとする HookGroup 配列のマップ。
 * z.record + HookEventTypeSchema で不正なイベント種別を拒否する。
 */
// zod 4 の z.record(enum, v) は全キー必須（部分オブジェクトをパース不可）になったため、
// 一部イベントのみ持つ部分マップを許容する z.partialRecord を使う（旧挙動を維持）。
export const WorkspaceHooksSchema = z
    .partialRecord(HookEventTypeSchema, z.array(HookGroupSchema))
    .describe('イベント種別ごとのフック設定マップ。各コマンドはコンテナサンドボックス内で実行される。' +
    '発火タイミング — SessionStart: タスク開始時 / UserPromptSubmit: ユーザー発言受理時（denyで差し戻し可） / ' +
    'PreToolUse: ツール実行直前（denyで実行をブロック可） / PostToolUse・PostToolUseFailure: ツール実行後（成功/失敗） / ' +
    'Stop・StopFailure: 応答ターン完了時（Stopはdenyで継続指示可） / SubagentStart・SubagentStop: サブエージェント開始・終了時 / ' +
    'PreCompact・PostCompact: コンテキスト圧縮の前後 / ApprovalRequired: ツール承認待ち開始時 / ' +
    'UserInputRequired: 質問回答待ち開始時。ブロック効果（deny）を持つのはPreToolUse・Stop・UserPromptSubmitの3種のみで、' +
    '他はAIへの追加コンテキスト伝達・監査記録のみに使われる。');
//# sourceMappingURL=workspace-hooks-schemas.js.map